import React, { useRef, useState } from "react";
import { Upload, Loader2, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/components/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { AVATAR_PRESETS, resizeImageFile } from "@/lib/avatars";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Pick a built-in avatar, upload a photo, or go back to the sign-in photo /
// initials (2026-10-03). Saves via PUT /api/auth/me/avatar.
export function AvatarPicker({ isLight }) {
  const { user, checkAuth } = useAuth() || {};
  const fileRef = useRef(null);
  const [busy, setBusy] = useState("");
  const current = user?.avatar || "";

  const save = async (avatar, key) => {
    setBusy(key);
    try {
      const r = await fetch(`${API}/auth/me/avatar`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ avatar }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Couldn't save your avatar.");
      await checkAuth?.();
      toast.success("Avatar updated");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    try {
      save(await resizeImageFile(file), "upload");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const tile = (selected) => `relative grid place-items-center rounded-2xl p-1 transition-all ${selected ? "ring-2 ring-[#A67C52] ring-offset-2 " + (isLight ? "ring-offset-white" : "ring-offset-slate-900") : "hover:scale-105"}`;
  const muted = isLight ? "text-slate-500" : "text-neutral-400";

  return (
    <div className="space-y-3" data-testid="avatar-picker">
      <div className="flex items-center gap-3">
        <UserAvatar user={user} size={56} className="rounded-2xl shadow-lg" />
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="avatar-upload" onClick={() => fileRef.current?.click()} disabled={!!busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#A67C52] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#8A6640] disabled:opacity-60">
            {busy === "upload" ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />} Upload photo
          </button>
          {current && (
            <button type="button" data-testid="avatar-reset" onClick={() => save("", "reset")} disabled={!!busy}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-bold disabled:opacity-60 ${isLight ? "border-slate-300 text-slate-700" : "border-white/15 text-white"}`}>
              <RotateCcw className="size-3.5" /> {user?.picture ? "Use my sign-in photo" : "Use my initials"}
            </button>
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="avatar-file"
          onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      <p className={`text-[11px] ${muted}`}>Or pick one:</p>
      <div className="grid grid-cols-6 gap-2.5">
        {AVATAR_PRESETS.map((p) => {
          const value = `preset:${p.id}`;
          const selected = current === value;
          return (
            <button key={p.id} type="button" data-testid={`avatar-preset-${p.id}`} aria-label={p.label} aria-pressed={selected}
              onClick={() => !selected && save(value, p.id)} disabled={!!busy} className={tile(selected)}>
              {busy === p.id ? <Loader2 className="size-5 animate-spin" /> : <UserAvatar user={{ avatar: value }} size={40} className="rounded-xl" />}
              {selected && <span className="absolute -right-1 -top-1 grid size-4 place-items-center rounded-full bg-[#22C55E] text-white"><Check className="size-3" /></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
